"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Zap, Shield } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function StrengthCardPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-orange-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-orange-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-orange-500">/</span>
            <span className="text-sm">Majör Arkana</span>
            <span className="text-orange-500">/</span>
            <span className="text-sm">Strength</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/major-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Majör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Strength</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-orange-900/20 to-amber-900/20 rounded-xl p-8 mb-8 border border-orange-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/rider-waite-strength.svg"
                  alt="Strength Tarot Card"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">VIII</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">Strength</h1>
                  <p className="text-xl text-orange-400">Güç / İçsel Kuvvet</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                İçsel güç, cesaret ve nazik güçlülüğün kartı. Fiziksel güçten ziyade ruhsal güçlülük
                ve sevgiyle zorluklara karşı durmanın temsilidir.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm">İçsel güç</span>
                <span className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm">Cesaret</span>
                <span className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm">Şefkat</span>
                <span className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm">Sabır</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İçsel Güç</h3>
                  <p className="text-slate-400 text-sm">Ruhsal kuvvet, iç huzur ve kendine güven.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Nazik Güçlülük</h3>
                  <p className="text-slate-400 text-sm">Sevgi ve şefkatle zorlukların üstesinden gelme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Cesaret</h3>
                  <p className="text-slate-400 text-sm">Korkularla yüzleşme, brave davranış.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sabır ve Hoşgörü</h3>
                  <p className="text-slate-400 text-sm">Anlayışlı yaklaşım, empati ve duygusal zeka.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Shield className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Güçsüzlük</h3>
                  <p className="text-slate-400 text-sm">İç güç eksikliği, kendine güvensizlik.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Öz-Şüphe</h3>
                  <p className="text-slate-400 text-sm">Yeteneklerine güvenmeme, korku ve çekince.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sabırsızlık</h3>
                  <p className="text-slate-400 text-sm">Acele etme, tolere edememe, sinirlilik.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Ham Güç</h3>
                  <p className="text-slate-400 text-sm">Kontrol eksikliği, saldırganlık, zorbalık.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Sembolik Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👩</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kadın Figürü</h3>
              <p className="text-slate-400 text-sm">Feminen güç, şefkat ve nazik kuvvet.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🦁</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Aslan</h3>
              <p className="text-slate-400 text-sm">Ham güç, tutku ve kontrol edilmesi gereken içgüdüler.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-black text-lg">♾️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Sonsuzluk Sembolü</h3>
              <p className="text-slate-400 text-sm">Ruhsal güç, sınırsız potansiyel ve divine bağlantı.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌹</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Gül Çelengi</h3>
              <p className="text-slate-400 text-sm">Aşk, tutkuların dönüşümü ve güzellik.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">⛰️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Dağlar</h3>
              <p className="text-slate-400 text-sm">Aştığı zorluklar, kararlılık ve sağlamlık.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">☀️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Altın Işık</h3>
              <p className="text-slate-400 text-sm">Divine enerji, pozitivite ve ruhsal aydınlanma.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Şefkatli ve anlayışlı ilişki</li>
                <li>• Zorlukları birlikte aşma</li>
                <li>• Sabırlı ve hoşgörülü sevgi</li>
                <li>• Partner desteği ve güç verme</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Zorluklarla başa çıkma</li>
                <li>• Ekip çalışmasında liderlik</li>
                <li>• Sabırlı ve istikrarlı çalışma</li>
                <li>• İnsan ilişkilerinde başarı</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sun className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İçsel gücü keşfetme</li>
                <li>• Korkularla yüzleşme</li>
                <li>• Empati geliştirme</li>
                <li>• Ruhsal güçlenme</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Zap className="h-5 w-5 text-blue-400" />
                Sağlık ve Güç
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İyileşme süreci</li>
                <li>• Mental ve duygusal güç</li>
                <li>• Stresle başa çıkma</li>
                <li>• Hayat gücü artışı</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite/major-arcana/chariot"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            The Chariot
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/major-arcana/hermit"
            className="inline-flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
          >
            The Hermit
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}